import { EUROPACE_PAYMENT_DATES, SALARY_CALCULATION_OPTIONS } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_Get,
    SchemaLoanRequestResolver_GetOfferRequestConditionsLatest,
} from 'cpool-tables/backend-resolvers';

import { prepareLiabilities } from './prepareLiabilities';
import { calculateSalary } from '../DataSharing/utils/calculateSalary';
import { residualDebtInsuranceDefaultValues } from '../ResidualDebtInsurance/defaultValues';
import type { SalaryValues } from '../types';

export const prepareDefaultValues = ({
    lead,
    requestConditions,
    salaryValues,
}: {
    lead: SchemaLoanRequestResolver_Get;
    requestConditions: SchemaLoanRequestResolver_GetOfferRequestConditionsLatest | undefined;
    salaryValues: SalaryValues;
}) => ({
    carFinance: requestConditions?.carFinance
        ? requestConditions.carFinance
        : {
              downPayment: lead.finanzbedarf.fahrzeugkauf?.anzahlung ?? undefined,
              financingWish: lead.finanzbedarf.fahrzeugkauf?.finanzierungswunsch ?? undefined,
              vehiclePrice: lead.finanzbedarf.fahrzeugkauf?.fahrzeugPreis ?? undefined,
              vehicleType: lead.finanzbedarf.fahrzeugkauf?.fahrzeugTyp ?? undefined,
          },
    dataSnippets: {
        applicant1: {
            excludeApplicant: requestConditions?.dataSnippets?.applicant1?.excludeApplicant ?? false,
            rentFree: requestConditions?.dataSnippets?.applicant1?.rentFree ?? false,
            salary: calculateSalary({
                applicant: 1,
                salaryMethod:
                    requestConditions?.dataSnippets?.applicant1?.salaryMode ??
                    SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
                salaryValues,
            }),
            salaryMode:
                requestConditions?.dataSnippets?.applicant1?.salaryMode ??
                SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
            withoutMaintenanceIncome: requestConditions?.dataSnippets?.applicant1?.withoutMaintenanceIncome ?? false,
            withoutOtherIncome: requestConditions?.dataSnippets?.applicant1?.withoutOtherIncome ?? false,
            withoutRentIncome: requestConditions?.dataSnippets?.applicant1?.withoutRentIncome ?? false,
            withoutSidejob: requestConditions?.dataSnippets?.applicant1?.withoutSidejob ?? false,
        },
        applicant2: {
            excludeApplicant: requestConditions?.dataSnippets?.applicant2?.excludeApplicant ?? false,
            rentFree: requestConditions?.dataSnippets?.applicant2?.rentFree ?? false,
            salary: calculateSalary({
                applicant: 2,
                salaryMethod:
                    requestConditions?.dataSnippets?.applicant2?.salaryMode ??
                    SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
                salaryValues,
            }),
            salaryMode:
                requestConditions?.dataSnippets?.applicant2?.salaryMode ??
                SALARY_CALCULATION_OPTIONS.LAST_MONTHS_SALARY,
            withoutMaintenanceIncome: requestConditions?.dataSnippets?.applicant2?.withoutMaintenanceIncome ?? false,
            withoutOtherIncome: requestConditions?.dataSnippets?.applicant2?.withoutOtherIncome ?? false,
            withoutRentIncome: requestConditions?.dataSnippets?.applicant2?.withoutRentIncome ?? false,
            withoutSidejob: requestConditions?.dataSnippets?.applicant2?.withoutSidejob ?? false,
        },
    },
    kreditBetrag: requestConditions?.requestAmount ?? lead.finanzbedarf.finanzierungswunsch.kreditbetrag,
    kreditLaufzeit: requestConditions?.runtimeInMonths ?? lead.finanzbedarf.finanzierungswunsch.laufzeitInMonaten,
    liabilityStates: requestConditions?.liabilityStates ?? [],
    provision: requestConditions?.commissionRequestInPercent ?? 3,
    ratenzahlungstermin: requestConditions?.paymentDate ?? EUROPACE_PAYMENT_DATES.MONATSENDE,
    restschuldversicherung: requestConditions?.residualDebtInsurance ?? residualDebtInsuranceDefaultValues,
    verwendungszweck: requestConditions?.financingPurpose ?? lead.finanzbedarf.finanzierungszweck,
    ...prepareLiabilities(lead.haushalt.verbindlichkeiten, requestConditions?.liabilityStates),
});
